import { PUBLIC_SEARCH_Q_MAX, type NotaPublicaCard } from '@ahora-patagonia/contracts';
import { api } from '../../shared/http/ApiClient.js';
import { BaseViewModel } from '../../shared/viewmodels/BaseViewModel.js';
import { BusquedaRepository } from '../models/repositories/BusquedaRepository.js';
import type { BusquedaData } from '../models/entities/BusquedaData.js';

interface State {
  loading: boolean;
  /** La última página traída (shell, total, lateral, avisos). */
  data: BusquedaData | null;
  /** Notas acumuladas: "Ver más" appendea de a 12, no reemplaza. */
  items: NotaPublicaCard[];
  cargandoMas: boolean;
  error: string | null;
  /** El término ya buscado (para no repetir el fetch de la misma q). */
  q: string;
  hydrated: boolean;
}

export function limpiarTermino(q: string): string {
  return q.trim().slice(0, PUBLIC_SEARCH_Q_MAX);
}

/**
 * ViewModel de la búsqueda **incremental**: la pantalla arranca con todas las noticias y cada
 * letra que el lector escribe (o borra) recorta la lista.
 *
 * Hidratado del SSR para la q inicial. De ahí en adelante cada cambio del término dispara un
 * pedido; el rebote lo pone la vista, que es donde vive el ritmo de tipeo.
 */
export class BusquedaViewModel extends BaseViewModel<State> {
  private readonly repo = new BusquedaRepository({ get: (p) => api.get(p) });
  /** Último término pedido. Lo que vuelva de un término anterior se descarta. */
  private ultimaPedida: string;

  constructor(initial: BusquedaData | null, qInicial: string) {
    const q = initial?.q ?? qInicial;
    super({
      loading: false,
      data: initial,
      items: initial?.items ?? [],
      cargandoMas: false,
      error: null,
      q,
      hydrated: initial !== null,
    });
    this.ultimaPedida = q;
  }

  async buscar(q: string): Promise<void> {
    const limpio = limpiarTermino(q);
    const actual = this.state$.value;
    if (actual.hydrated && actual.q === limpio && actual.data) return;

    this.ultimaPedida = limpio;
    // `items` **no** se vacía acá: mientras se escribe, la lista anterior se queda a la vista
    // hasta que llega la nueva. Vaciarla en cada tecla haría colapsar la pantalla y volver a
    // crecer, que es justo el parpadeo que una búsqueda incremental tiene que evitar.
    this.patch({ loading: true, error: null, q: limpio });
    try {
      const data = await this.repo.buscar(limpio);
      // Escribiendo rápido salen varios pedidos y pueden volver desordenados: si mientras este
      // viajaba ya se pidió otro término, su resultado es viejo y pisaría la lista buena.
      if (this.ultimaPedida !== limpio) return;
      this.patch({ data, items: data.items, loading: false, hydrated: true });
    } catch (err) {
      if (this.ultimaPedida !== limpio) return;
      this.patch({ loading: false, error: (err as Error).message });
      this.emit('error', err);
    }
  }

  get hayMas(): boolean {
    const { data, items } = this.state$.value;
    return data !== null && items.length < data.total;
  }

  async verMas(): Promise<void> {
    const { data, items, cargandoMas, q } = this.state$.value;
    if (!data || cargandoMas || !this.hayMas) return;

    this.patch({ cargandoMas: true });
    try {
      const siguiente = Math.floor(items.length / data.pageSize) + 1;
      const pagina = await this.repo.buscar(q, siguiente);
      // Si el término cambió mientras viajaba la página, ya no corresponde a esta lista.
      if (this.ultimaPedida !== q) {
        this.patch({ cargandoMas: false });
        return;
      }
      const vistos = new Set(items.map((n) => n.id));
      this.patch({
        data: pagina,
        items: [...items, ...pagina.items.filter((n) => !vistos.has(n.id))],
        cargandoMas: false,
      });
    } catch (err) {
      this.patch({ cargandoMas: false, error: (err as Error).message });
      this.emit('error', err);
    }
  }
}
